import {
    ChangeDetectionStrategy,
    signal,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { isDefined } from '@fi-shared/utils';
import {
    FiVimeoDataService,
    FiVimeoVideoStatus,
    IVimeoDataItem,
} from '@fi-shared/media-bank/fi-vimeo/fi-vimeo-data.service';
import { BehaviorSubject, interval, Subject } from 'rxjs';
import { FiVimeoPreviewPicture } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo';
import { debounce, takeUntil } from 'rxjs/operators';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiVimeoService } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo.service';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { FiImageSize } from '@fi-shared/types';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiVimeoPlayerApi, IVideoProgressEvent } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo-player-api';

interface IVimeoVideoData {
    videoId: string;
    videoMaxHeight: number;
    videoMaxWidth: number;
    videoContainerWidth: number;
    videoContainerHeight: number;
    previewPicture: FiVimeoPreviewPicture;
}

@Component({
    selector: 'fi-media-viewer-vimeo',
    templateUrl: './media-viewer-vimeo.component.html',
    styleUrls: ['./media-viewer-vimeo.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-media-viewer-vimeo' },
    standalone: false,
})
export class MediaViewerVimeoComponent implements OnInit, OnDestroy {
    @Input() mediaBankFileReference: IMediaBankFileReference;
    @Input() isCurrentMediaBank: boolean = false;
    @Input() listenToProgress: boolean = false;
    @Output() onSetBackgroundColor: EventEmitter<HTMLImageElement> = new EventEmitter<HTMLImageElement>();
    @Output() onProgress: EventEmitter<IVideoProgressEvent> = new EventEmitter<IVideoProgressEvent>();
    private videoIframe: ElementRef<HTMLIFrameElement>;
    @ViewChild('videoIframe') set videoIframeElement(value: ElementRef<HTMLIFrameElement>) {
        if (value) {
            this.videoIframe = value;
        }
    }

    private iframeOverlayElemRef: ElementRef;
    @ViewChild('iframeOverlay') set iframeOverlay(element: ElementRef) {
        if (element) {
            this.iframeOverlayElemRef = element;
        }
    }

    private renderer = inject(Renderer2);
    private vimeoDataService = inject(FiVimeoDataService);
    private vimeoService = inject(FiVimeoService);
    private detectDevicesService = inject(DetectDevicesService);
    private elementRef = inject(ElementRef);
    private fiBrowserEventsService = inject(FiBrowserEventsService);
    private cdr = inject(ChangeDetectorRef);
    private mediaStorageService = inject(FiMediaStorageService);

    protected vimeoVideoData: IVimeoVideoData;
    private videoStatus$ = new BehaviorSubject<FiVimeoVideoStatus>(FiVimeoVideoStatus.Processing);
    protected loading = signal<boolean>(true);
    protected readonly isMobilePhone: boolean = this.detectDevicesService.isMobilePhone();
    private readonly playerId: string = `vimeo-player-${Math.random().toString(36).substring(2, 9)}`;
    private vimeoPlayerApi: FiVimeoPlayerApi;
    protected vimeoSafeUrl: SafeResourceUrl;

    public ngOnInit(): void {
        // Set the Vimeo URL once - it should never change
        const videoId = this.mediaBankFileReference.PhysicalFileName;
        this.vimeoSafeUrl = this.vimeoService.getVimeoSafeUrl(videoId, this.isCurrentMediaBank, this.playerId);

        this.setVimeoData();
        this.subscribeOnResize();
    }

    private getMediaBankFileRefUrl(): string {
        return this.mediaStorageService.getMediaFileUrl(this.mediaBankFileReference, { size: FiImageSize.Large });
    }

    protected stopPropagation($event: Event) {
        $event.stopPropagation();
    }

    protected setBackgroundColor(videoPreviewImage: HTMLImageElement) {
        this.onSetBackgroundColor.emit(videoPreviewImage);
    }

    private subscribeOnResize(): void {
        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(
                debounce(() => interval(200)),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe(() => {
                this.setVimeoVideosSizes();
                this.cdr.detectChanges();
            });
    }

    private setVimeoData(): void {
        const vimeoVideoData: IVimeoVideoData = {
            videoId: this.mediaBankFileReference.PhysicalFileName,
            videoMaxHeight: 0,
            videoMaxWidth: 0,
            videoContainerHeight: 0,
            videoContainerWidth: 0,
            previewPicture: undefined,
        };
        this.vimeoDataService
            .getVimeoData$(vimeoVideoData.videoId)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((vimeoDataItem: IVimeoDataItem) => {
                if (vimeoDataItem.status === FiVimeoVideoStatus.Ok) {
                    if (vimeoDataItem.previewPictures && vimeoDataItem.previewPictures.length) {
                        vimeoVideoData.previewPicture = vimeoDataItem.previewPictures[0];
                        vimeoVideoData.videoMaxHeight = vimeoDataItem.height;
                        vimeoVideoData.videoMaxWidth = vimeoDataItem.width;
                    }
                }

                this.videoStatus$.next(vimeoDataItem.status);
                this.vimeoVideoData = Object.assign({}, vimeoVideoData);
                this.cdr.markForCheck();
                this.loading.set(false);

                setTimeout(() => {
                    this.setVimeoVideosSizes();
                    if (this.listenToProgress) {
                        this.initializeVimeoPlayerApi();
                    }
                });
            });
    }

    private setMobileSizes(): void {
        const sizes: { width?: number; height?: number } = this.getMobileVideoSize(
            this.vimeoVideoData.videoMaxHeight,
            this.vimeoVideoData.videoMaxWidth,
        );

        // square
        if (!isDefined(sizes)) {
            return;
        }

        // vertical
        if (isDefined(sizes.width)) {
            this.renderer.setStyle(this.videoIframe.nativeElement, 'width', `${sizes.width}px`);
        }

        // horizontal
        if (isDefined(sizes.height)) {
            this.renderer.setStyle(this.videoIframe.nativeElement, 'height', `${sizes.height}px`);

            if (this.iframeOverlayElemRef) {
                this.renderer.setStyle(this.iframeOverlayElemRef.nativeElement, 'height', `${sizes.height - 50}px`);
            }
        }
    }

    private getMobileVideoSize(maxHeight: number, maxWidth: number): { height?: number; width?: number } {
        const coef: number = 9 / 16;
        if (maxHeight > maxWidth) {
            return { width: this.elementRef.nativeElement.parentElement.offsetHeight * coef };
        }

        if (maxWidth > maxHeight) {
            return { height: this.elementRef.nativeElement.parentElement.offsetWidth * coef };
        }
    }

    protected isVideoAvailable(): boolean {
        return isDefined(this.vimeoVideoData) && this.videoStatus$.value === FiVimeoVideoStatus.Ok;
    }

    private setVimeoVideosSizes(): void {
        if (!isDefined(this.videoIframe) || !this.isVideoAvailable()) {
            return;
        }

        if (this.isMobilePhone) {
            this.setMobileSizes();
        }
    }

    private initializeVimeoPlayerApi(): void {
        if (!this.videoIframe?.nativeElement) {
            return;
        }

        const iframe = this.videoIframe.nativeElement;

        this.vimeoPlayerApi = new FiVimeoPlayerApi();
        this.vimeoPlayerApi.initialize(this.playerId, iframe);

        this.vimeoPlayerApi
            .getProgress$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((progress: IVideoProgressEvent) => {
                this.onProgress.emit(progress);
            });

        // Wait for iframe to load before setting up listeners
        // This ensures the Vimeo player is initialized and ready
        iframe.addEventListener(
            'load',
            () => {
                setTimeout(() => this.vimeoPlayerApi.setupEventListeners(), 100);
            },
            { once: true },
        );
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        if (this.vimeoPlayerApi) {
            this.vimeoPlayerApi.destroy();
        }
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
